import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "apexframez_cart_v3";

const optionsKey = (options) =>
  options
    ? Object.keys(options)
        .sort()
        .map((k) => `${k}:${options[k]}`)
        .join("|")
    : "";

const cartIdFor = (slug, options) =>
  optionsKey(options) ? `${slug}__${optionsKey(options)}` : slug;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // product, qty, options { lighting, build }, unitPrice (already adjusted)
  const addItem = (product, qty = 1, options = null, unitPrice = null) => {
    const cart_id = cartIdFor(product.slug, options);
    const finalUnit = unitPrice ?? product.price_inr;
    setItems((prev) => {
      const existing = prev.find((p) => p.cart_id === cart_id);
      if (existing) {
        return prev.map((p) =>
          p.cart_id === cart_id ? { ...p, quantity: p.quantity + qty } : p
        );
      }
      return [
        ...prev,
        {
          cart_id,
          slug: product.slug,
          name: product.name,
          unit_price: finalUnit,
          image: product.image,
          team_color: product.team_color,
          quantity: qty,
          options: options || null,
        },
      ];
    });
  };

  const removeItem = (cart_id) =>
    setItems((prev) => prev.filter((p) => p.cart_id !== cart_id));

  const updateQty = (cart_id, quantity) =>
    setItems((prev) =>
      prev
        .map((p) => (p.cart_id === cart_id ? { ...p, quantity } : p))
        .filter((p) => p.quantity > 0)
    );

  const clear = () => setItems([]);

  const total = useMemo(
    () => items.reduce((s, p) => s + p.unit_price * p.quantity, 0),
    [items]
  );
  const count = useMemo(
    () => items.reduce((s, p) => s + p.quantity, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, total, count, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
